import React, { useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

const unitExtra = (it) => ({
  freight_unit: (it.freight || 0) / (it.quantity || 1),
  other_unit: (it.other || 0) / (it.quantity || 1),
});
const salePrice = (it, margin) => {
  const e = unitExtra(it);
  return +((it.unit_cost + e.freight_unit + e.other_unit) * (1 + Number(margin || 0) / 100)).toFixed(2);
};

const ItemRow = ({ it, idx, products, margin, onChange }) => (
  <TableRow data-testid={`nfe-item-${idx}`}>
    <TableCell className="text-xs max-w-[220px]">
      <div className="font-medium text-sm">{it.name}</div>
      <div className="text-slate-500">Cód. {it.code || "—"}{it.ean ? ` • EAN ${it.ean}` : ""}</div>
    </TableCell>
    <TableCell className="min-w-[200px]">
      <Select value={it.product_id} onValueChange={(v) => onChange(idx, { product_id: v })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`nfe-map-${idx}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="new">+ Cadastrar como novo produto</SelectItem>
          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {it.match_id && it.product_id === it.match_id && <Badge variant="outline" className="mt-1 text-[10px] border-emerald-500 text-emerald-700">vinculado automaticamente</Badge>}
    </TableCell>
    <TableCell className="text-right kpi-num text-sm">{it.quantity} {it.unit || ""}</TableCell>
    <TableCell className="text-right kpi-num text-sm">{brl(it.unit_cost)}</TableCell>
    <TableCell className="text-right kpi-num text-xs">{brl(unitExtra(it).freight_unit + unitExtra(it).other_unit)}</TableCell>
    <TableCell className="text-right">
      {it.product_id === "new" ? (
        <Input type="number" step="0.01" className="h-8 w-24 text-right ml-auto" placeholder={salePrice(it, margin).toFixed(2)}
          value={it.sale_price ?? ""} onChange={(e) => onChange(idx, { sale_price: e.target.value })} data-testid={`nfe-price-${idx}`} />
      ) : <span className="text-xs text-slate-500">mantém preço</span>}
    </TableCell>
  </TableRow>
);

const UploadStep = ({ loading, onFile }) => (
  <div className="space-y-3 text-sm">
    <p className="text-slate-600">Envie o arquivo <b>XML</b> da NF-e que o fornecedor enviou. Os itens, o fornecedor, o frete e a chave de acesso são lidos automaticamente.</p>
    <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-8 cursor-pointer hover:border-blue-500 transition">
      {loading ? <Loader2 className="w-8 h-8 animate-spin text-blue-600" /> : <FileUp className="w-8 h-8 text-slate-400" />}
      <span className="text-slate-600">{loading ? "Lendo nota..." : "Clique para escolher o XML"}</span>
      <input type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={onFile} data-testid="nfe-xml-input" />
    </label>
    <p className="text-xs text-slate-500">A consulta automática só pela chave de acesso exige um serviço de consulta de NF-e contratado e será ativada quando você tiver a conta.</p>
  </div>
);

const NfeSummary = ({ nfe }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm border rounded-lg p-3 bg-slate-50" data-testid="nfe-summary">
    <div><div className="text-xs text-slate-500">NF-e</div><div className="font-medium">nº {nfe.number} {nfe.series ? `• série ${nfe.series}` : ""}</div>
      <div className="font-mono text-[10px] break-all text-slate-500">{nfe.nfe_key || "sem chave"}</div></div>
    <div><div className="text-xs text-slate-500">Emitente</div><div className="font-medium">{nfe.supplier.legal_name || nfe.supplier.name}</div>
      <div className="text-xs text-slate-500">{nfe.supplier.document}</div></div>
    <div><div className="text-xs text-slate-500">Total da nota</div><div className="kpi-num text-lg text-blue-600">{brl(nfe.total)}</div>
      <div className="text-xs text-slate-500">Frete {brl(nfe.freight)} • Desconto {brl(nfe.discount)}</div></div>
  </div>
);

const NfeOptions = ({ o, suppliers }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
    <div><Label>Fornecedor</Label>
      <Select value={o.supplierId} onValueChange={o.setSupplierId}>
        <SelectTrigger data-testid="nfe-supplier"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="new">+ Cadastrar emitente da nota</SelectItem>
          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select></div>
    <div><Label>Margem % (novos produtos)</Label>
      <Input type="number" value={o.margin} onChange={(e) => o.setMargin(e.target.value)} data-testid="nfe-margin" /></div>
    <div><Label>Situação</Label>
      <Select value={o.status} onValueChange={o.setStatus}>
        <SelectTrigger data-testid="nfe-status"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="received">Já recebida (entra no estoque)</SelectItem>
          <SelectItem value="pending">A receber</SelectItem>
        </SelectContent>
      </Select></div>
    <div><Label>Vencimento (conta a pagar)</Label>
      <Input type="date" value={o.dueDate} onChange={(e) => o.setDueDate(e.target.value)} data-testid="nfe-due" /></div>
  </div>
);

const supplierPayload = (s) => ({
  name: s.name || s.legal_name || "Fornecedor", document: s.document, phone: s.phone, address: s.address,
  notes: s.legal_name ? `Razão social: ${s.legal_name}` : null,
});

const buildImportPayload = (nfe, items, o) => {
  const subtotal = items.reduce((s, it) => s + it.quantity * it.unit_cost, 0);
  return {
    nfe_key: nfe.nfe_key, status: o.status, due_date: o.dueDate || null,
    supplier_id: o.supplierId === "new" ? null : o.supplierId,
    supplier: o.supplierId === "new" ? supplierPayload(nfe.supplier) : null,
    freight: Math.max(0, +(nfe.total - subtotal + (nfe.discount || 0)).toFixed(2)),
    discount: nfe.discount || 0,
    notes: `NF-e nº ${nfe.number || ""}${nfe.series ? ` série ${nfe.series}` : ""}`,
    items: items.map((it) => {
      const isNew = it.product_id === "new";
      return {
        product_id: isNew ? null : it.product_id, name: it.name, code: it.code, ean: it.ean,
        quantity: it.quantity, cost_price: it.unit_cost, ...unitExtra(it), margin_pct: Number(o.margin),
        sale_price: isNew ? (Number(it.sale_price) || salePrice(it, o.margin)) : null,
      };
    }),
  };
};

export const NfeImportDialog = ({ open, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [nfe, setNfe] = useState(null);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("new");
  const [margin, setMargin] = useState(50);
  const [status, setStatus] = useState("received");
  const [dueDate, setDueDate] = useState("");
  const opts = { supplierId, setSupplierId, margin, setMargin, status, setStatus, dueDate, setDueDate };

  useEffect(() => {
    if (!open) return;
    setNfe(null); setItems([]); setStatus("received"); setDueDate("");
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.type === "product")));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, [open]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post("/purchases/nfe/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNfe(data);
      setItems(data.items.map((i) => ({ ...i, product_id: i.match_id || "new", sale_price: "" })));
      setSupplierId(data.supplier.existing_id || "new");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao ler o XML"); }
    finally { setLoading(false); }
  };

  const onChange = (idx, patch) => setItems((its) => its.map((it, j) => (j === idx ? { ...it, ...patch } : it)));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/purchases/nfe/import", buildImportPayload(nfe, items, opts));
      toast.success(`NF-e importada${data.created_products ? ` — ${data.created_products} produto(s) novo(s)` : ""}`);
      onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="nfe-import-dialog">
        <DialogHeader><DialogTitle className="font-display">Importar produtos da NF-e</DialogTitle></DialogHeader>
        {!nfe ? <UploadStep loading={loading} onFile={onFile} /> : (
          <div className="space-y-4">
            <NfeSummary nfe={nfe} />
            <NfeOptions o={opts} suppliers={suppliers} />
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Item da nota</TableHead><TableHead>Produto no sistema</TableHead>
                  <TableHead className="text-right">Qtd</TableHead><TableHead className="text-right">Custo un.</TableHead>
                  <TableHead className="text-right">Frete+outros/un</TableHead><TableHead className="text-right">Preço venda</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.map((it, idx) => <ItemRow key={`${it.code}-${idx}`} it={it} idx={idx} products={products} margin={margin} onChange={onChange} />)}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-slate-500">O frete e os outros custos da nota (IPI, ST, seguro) são rateados por unidade e salvos na Tabela de Precificação.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {nfe && <Button onClick={submit} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="nfe-import-btn">Importar {items.length} itens</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
